import { Injectable, InjectionToken } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { FamilyMemberFormService } from 'src/app/_services/family-member-form.service';
import { APIResponse } from 'src/app/_services/types/api-response.type';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { InsuranceTypeEnum } from 'src/app/enums/insurance-type.enum';

import { ProposalService } from '../../_services/proposal.service';
import { QuestionnaireAnswers } from '../../_services/types/building.types';
import {
  BuildingInsuranceMetatadata,
  CreateProposalResponse,
} from '../../_services/types/proposals.type';
import { BaseBuildingService } from './base-building.service';
import {
  BUILDING_INPUT_FORM_BASE,
  BUILDING_INPUT_FORM_STEPS,
  BUILDING_INSURANCE_INPUT,
} from './constants';

export const BUILDING_INSURANCE_SERVICE_TOKEN = new InjectionToken<BaseBuildingService>(
  'BaseBuildingService',
);

@Injectable({
  providedIn: 'root',
})
export class BuildingService extends BaseBuildingService {
  constructor(
    router: Router,
    proposalService: ProposalService,
    familyMemberService: FamilyMemberFormService,
    route: ActivatedRoute,
    authService: UserAuthService,
  ) {
    super(router, proposalService, familyMemberService, route, authService);
    this.router.navigateByUrl(BUILDING_INPUT_FORM_BASE + BUILDING_INSURANCE_INPUT, {
      replaceUrl: true,
    });
    this.handleQueryParams();

    this.navigationChangeSubscription = this.router.events.subscribe((val) => {
      if (!(val instanceof NavigationEnd)) return;
      if (
        val.urlAfterRedirects.includes(BUILDING_INPUT_FORM_BASE) &&
        val.urlAfterRedirects.includes(BUILDING_INPUT_FORM_STEPS.INSURANCE_FORM.url)
      )
        this.handleQueryParams();
    });
  }

  sendBuildingInsuranceRequest(
    questionnaire: QuestionnaireAnswers,
  ): Observable<APIResponse<CreateProposalResponse>> {
    const metadata: BuildingInsuranceMetatadata = {
      type: InsuranceTypeEnum.BUILDING,
      ...this.buildingFormData.toMetadata(),
      pastInsured: questionnaire.hasInsurance,
      companyName: questionnaire.insurance,
      reason: questionnaire.reason,
      anyClaims: questionnaire.hasClaims,
      claimDetails: questionnaire.claims,
    };

    const payload = {
      memberId: this.familyMemberService.mainMemberId,
      insuranceType: InsuranceTypeEnum.BUILDING,
      metadata,
      originalProposalId: this.buildingFormData.originalProposalId,
      originalLeadId: this.buildingFormData.originalLeadId,
    };
    return this.proposalService.addProposalRequest(payload);
  }

  override handleQueryParams() {
    this.flowReady.next(false);
    if (this.route.snapshot.queryParamMap.get('leadId')) {
      const leadId: string = this.route.snapshot.queryParamMap.get('leadId');

      if (this.authService.isUserLoggedIn()) {
        this.proposalService.getLeadDetails(leadId).subscribe({
          next: (res) => {
            if (!res.data || !res.data.metadata) return;
            if (res.data.insuranceType !== InsuranceTypeEnum.BUILDING)
              throw Error('Insurance type does not match the flow');
            this.buildingFormData.fromLead(res.data);
            this.flowReady.next(true);
          },
        });
      } else this.flowReady.next(true);
    } else if (this.route.snapshot.queryParamMap.get('proposalId')) {
      const proposalId: string = this.route.snapshot.queryParamMap.get('proposalId');
      if (this.authService.isUserLoggedIn()) {
        this.proposalService.getProposalDetails(proposalId).subscribe({
          next: (res) => {
            if (!res.data) return;
            if (res.data.insuranceType !== InsuranceTypeEnum.BUILDING)
              throw Error('Insurance type does not match the flow');
            this.buildingFormData.originalProposalId = res.data.proposalId;
            // TODO: Handle the selected memberId if/once the flow is reworked to be able to choose the member
            this.fillFromUser().subscribe((v) => {
              if (v) {
                this.flowReady.next(true);
              }
            });
          },
        });
      } else this.flowReady.next(true);
    } else {
      this.fillFromUser().subscribe((v) => {
        if (v) {
          this.flowReady.next(true);
        }
      });
    }
  }
}
